import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useVault } from '../context/VaultContext';

export function useSetsList() {
  const { activeVaultId } = useVault();

  return useQuery({
    queryKey: ['sets', activeVaultId],
    queryFn: () => apiClient.listSets(activeVaultId ?? undefined),
  });
}

export function useSetDetail(setId: string) {
  return useQuery({
    queryKey: ['sets', setId],
    queryFn: () => apiClient.getSetDetail(setId),
    enabled: !!setId,
  });
}

export function useSetTemplates() {
  const { activeVault } = useVault();

  return useQuery({
    queryKey: ['set-templates', activeVault?.vaultType],
    queryFn: () => apiClient.getSetTemplates(activeVault?.vaultType),
  });
}

export function useCreateSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      platform?: string;
      items?: Array<{ name: string; platform?: string; category?: string }>;
    }) => apiClient.createSet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

export function useDeleteSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (setId: string) => apiClient.deleteSet(setId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

export function useAddSetItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, name, platform, category }: { setId: string; name: string; platform?: string; category?: string }) =>
      apiClient.addSetItem(setId, { name, platform, category }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sets', variables.setId] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

export function useRemoveSetItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, itemId }: { setId: string; itemId: string }) =>
      apiClient.removeSetItem(setId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sets', variables.setId] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

export function useToggleSetItemOwned() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      setId,
      itemId,
      isOwned,
      inventoryItemId,
    }: {
      setId: string;
      itemId: string;
      isOwned: boolean;
      inventoryItemId?: string | null;
    }) => apiClient.toggleSetItemOwned(setId, itemId, isOwned, inventoryItemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sets', variables.setId] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

export function useAutoMatchSetItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (setId: string) => apiClient.autoMatchSetItems(setId),
    onSuccess: (_, setId) => {
      queryClient.invalidateQueries({ queryKey: ['sets', setId] });
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}
